// FaqCenterPage — Full searchable FAQ center with categories from marketing
// Categories: General, Challenge, Account, Payouts, Rules, AI Bot, Platforms, Reset, Subscription

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCard, TfCardFloating, TfSectionHeaderView, TfStatRow, TfSearchBar } from '../../components/TfPrimitives';

interface Faq {
  q: string;
  a: string;
  category: string;
}

const FAQ: Faq[] = [
  // General
  { q: 'What is Funder Pro?', a: 'Funder Pro is a futures evaluation program built by traders, for traders. We give you a funded account, you trade it, and we split the profits 80/20 to 90/10 (in your favor). Headquartered in Malta (company C 104558).', category: 'General' },
  { q: 'How long does it take to get funded?', a: 'Once you pass the evaluation (reach 6% profit target) and complete KYC, your funded account is issued within 24 business hours. Typical path: 5-30 days from start to funded.', category: 'General' },
  { q: 'Where is Funder Pro based?', a: 'FunderPro Ltd., a Maltese-registered company (registration C 104558) at Cali House, Level 3, Vjal ir-Riħan, San Gwann, SGN 9020, Malta. Regulated by the Malta Financial Services Authority.', category: 'General' },
  { q: 'What is the pass rate?', a: 'Our historical pass rate is 10.14%, which is among the highest in the industry. We attribute this to clear rules and no over-engineered risk restrictions.', category: 'General' },
  { q: 'Do you offer a free trial?', a: 'We offer a 7-day money-back guarantee instead. Buy a challenge, trade for up to 7 days, and if you are not satisfied, request a full refund.', category: 'General' },

  // Challenge
  { q: 'What is the evaluation / challenge?', a: 'The challenge is a one-time evaluation where you trade a simulated account under our rules. Reach the 6% profit target without breaching daily loss or max drawdown to pass.', category: 'Challenge' },
  { q: 'How long do I have to pass?', a: 'There is no time limit. You can take 1 day or 100 days. However, we recommend keeping your trading activity focused. Resets are available to keep your days bankable.', category: 'Challenge' },
  { q: 'What is the profit target?', a: '6% of your account size: $3,000 for $50K, $6,000 for $100K, $9,000 for $150K. Calculated on starting balance.', category: 'Challenge' },
  { q: 'Can I trade the news?', a: 'Only outside the 2-minute blackout window. Per Section 13.9, you cannot open or close trades within 2 minutes of any high-impact news event. The bot auto-pauses.', category: 'Challenge' },
  { q: 'What is the consistency rule?', a: 'During evaluation, no single trading day may account for more than 40% of your profit target. In funded, the limit is 50%. If exceeded, excess profit is added to your target at 16:00 CT.', category: 'Challenge' },
  { q: 'Can I copy trades between my accounts?', a: "Yes, copy trading between your own challenge and funded accounts is allowed. Trading across other people's accounts is not.", category: 'Challenge' },

  // Account
  { q: 'How many accounts can I have?', a: 'You can have up to 5 active funded accounts plus unlimited passed accounts in the queue. Active accounts trade simultaneously; queued accounts are activated as previous ones close.', category: 'Account' },
  { q: 'What platforms are supported?', a: 'Deepchart, Rithmic, NinjaTrader, Tradovate, Sierra Chart, ATAS, Quantower, Bookmap, plus any DXFeed-supported platform. Level 1 data is free; L2 by exchange.', category: 'Account' },
  { q: 'What is the Daily Pause?', a: 'Positions auto-flatten at 16:00 CT. Account freezes until 16:05 CT the next trading day. Prevents overnight gap risk. Cannot be disabled.', category: 'Account' },
  { q: 'How do I reset a challenge?', a: 'From Dashboard → select the challenge → Reset. Reset clears P&L and open positions but keeps your remaining days. Costs $69/$129/$189 depending on tier.', category: 'Account' },
  { q: 'What happens on a drawdown breach?', a: 'If your EOD balance trails to your max DD level, the account is breached. You can purchase a new challenge. Active days counted toward the consistency rule are preserved.', category: 'Account' },

  // Payouts
  { q: 'How do I get paid?', a: 'Once you have 5 profitable trading days (min $100/day), request a payout from Dashboard → Wallet → Withdraw. Payments go via bank transfer, Wise, crypto, or PayPal within 24-48h.', category: 'Payouts' },
  { q: 'What is the profit split?', a: '80% from your first payout. After 3 successful payouts, your split increases to 90%. This is permanent for the lifetime of your account.', category: 'Payouts' },
  { q: 'Are there any payout fees?', a: 'Bank transfer and Wise are free. Crypto has a 1% network fee. PayPal has a 2% fee. We absorb all other processing costs.', category: 'Payouts' },
  { q: 'How often can I withdraw?', a: 'Every 5 profitable trading days. Each withdrawal request must be at least $100. You can withdraw any amount above $100 up to your account balance.', category: 'Payouts' },
  { q: 'What is the rewards program?', a: 'Earn $200 for every 5 profitable trading days during your first 3 rewards. After that, earn $100 minimum per 5-day cycle. No cap on rewards.', category: 'Payouts' },

  // AI Bot
  { q: 'How does the AI bot work?', a: 'The AI bot uses LLMs to interpret market structure and execute your strategy. You set risk parameters, max drawdown, and pairs. The bot trades automatically with your rules.', category: 'AI Bot' },
  { q: 'Is the AI bot allowed on all challenges?', a: 'Yes, AI bots are allowed on all tiers. You can also use EAs (Expert Advisors) as long as you own the source code. Third-party tick scalping EAs are prohibited.', category: 'AI Bot' },
  { q: 'Can I customize the AI strategy?', a: 'Yes — risk %, max drawdown, instruments traded, time of day, and on/off switch. All settings are stored locally and pushed via the worker on every change.', category: 'AI Bot' },

  // Subscription
  { q: 'What does my monthly fee include?', a: 'Unlimited challenge days, free activation, access to all 8 platforms, Level 1 data, and AI bot execution. Pro tier includes L2 data and API access.', category: 'Subscription' },
  { q: 'How do I cancel?', a: 'Cancel from Dashboard → Settings → Subscription. Your access continues until the end of your billing cycle. We do not prorate refunds on cancellation.', category: 'Subscription' },
  { q: 'Do you have a refund policy?', a: '7-day money-back guarantee on new, unused challenge accounts. After 7 days or after first trade, no refunds.', category: 'Subscription' },
  { q: 'What payment methods do you accept?', a: 'Visa, Mastercard, Amex, Apple Pay, Google Pay, bank transfer, and 20+ cryptocurrencies via Stripe and Coinbase.', category: 'Subscription' },
];

const CATEGORIES = ['General', 'Challenge', 'Account', 'Payouts', 'AI Bot', 'Subscription'];

export function FaqCenterPage() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [activeCat, setActiveCat] = useState<string>('General');
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const filtered = FAQ
    .filter((f) => f.category === activeCat)
    .filter((f) => q === '' || (f.q + f.a).toLowerCase().includes(q.toLowerCase()));

  const totalArticles = FAQ.length;
  const totalCats = CATEGORIES.length;
  const matchingCount = filtered.length;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">FAQ</div>
        <button onClick={() => nav('/portal/messages')} className="px-3 text-[15px] text-tf-blue">Contact</button>
      </div>

      {/* Hero — white floating card with article count + search + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>How can we help?</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>{totalArticles}</span>
            <span className="text-[15px] text-tf-text-3">articles</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="text-tf-text-3">Search the knowledge base · 24/7 support</span>
          </div>
          <div className="mt-3 px-1">
            <div className="relative">
              <input
                type="search"
                placeholder="Search articles..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="faq-search"
                className="w-full h-10 rounded-full bg-tf-surface-1 px-4 pl-10 text-[14px] text-tf-text-1 border border-tf-border focus:outline-none focus:ring-2 focus:ring-tf-blue/40"
                style={{ color: '#1C1C1E' }}
              />
              <svg viewBox="0 0 24 24" className="absolute left-3 top-2.5 h-5 w-5 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button onClick={() => nav('/portal/messages')} data-testid="hero-chat" className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Live chat
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => nav('/portal/messages')} data-testid="hero-email" className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Email
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
        </TfCardFloating>
      </div>

      {/* 3-up stat row */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Articles', value: `${totalArticles}`, color: '#007AFF' },
          { label: 'Categories', value: `${totalCats}`, color: '#1C1C1E' },
          { label: 'Matching', value: `${matchingCount}`, color: '#34C759' },
        ]}
      />

      {/* Categories */}
      <div className="px-4 pt-4 flex items-center gap-1.5 overflow-x-auto">
        {CATEGORIES.map((c) => (
          <button
            key={c} onClick={() => { setActiveCat(c); setOpenIdx(null); }}
            data-testid={`faq-cat-${c}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold transition',
              activeCat === c ? 'bg-tf-blue text-white' : 'bg-white border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >{c}</button>
        ))}
      </div>

      {/* FAQ list */}
      <TfSectionHeaderView title={`${activeCat} · ${filtered.length} articles`} />
      <div className="mx-4">
        <TfCard>
          {filtered.map((f, i) => {
            const open = openIdx === i;
            return (
              <div key={i} data-testid={`faq-${activeCat}-${i}`} className={['px-4 py-3', i < filtered.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
                <button
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="flex w-full items-start gap-2 text-left"
                >
                  <div className="text-[18px] mt-0.5">❓</div>
                  <div className="flex-1 text-[14px] font-semibold text-tf-text-1">{f.q}</div>
                  <svg viewBox="0 0 24 24" className={['h-4 w-4 text-tf-text-muted transition-transform mt-1', open ? 'rotate-90' : ''].join(' ')} fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                {open && <p className="mt-2 ml-7 text-[13px] text-tf-text-2 leading-relaxed">{f.a}</p>}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-4 py-8 text-center text-[12px] text-tf-text-3">No articles match your search. <button onClick={() => nav('/portal/messages')} className="text-tf-blue">Contact support →</button></div>
          )}
        </TfCard>
      </div>

      <div className="mx-4 mt-4">
        <TfCard className="p-4 text-center">
          <div className="text-[24px]">💬</div>
          <h3 className="text-[14px] font-bold text-tf-text-1 mt-1">Still need help?</h3>
          <p className="text-[12px] text-tf-text-3 mt-0.5">Our support team is available 24/7, including weekends.</p>
          <div className="mt-3 flex gap-2">
            <button onClick={() => nav('/portal/messages')} data-testid="open-chat" className="flex-1 h-10 rounded-full bg-tf-blue text-white text-[13px] font-semibold active:opacity-40">Live chat</button>
            <button onClick={() => nav('/portal/messages')} data-testid="email-support" className="flex-1 h-10 rounded-full bg-tf-surface-1 text-tf-text-1 text-[13px] font-semibold active:opacity-40">Email</button>
          </div>
        </TfCard>
      </div>
    </div>
  );
}
